import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { User, Phone, Trophy, Edit3, Save, Crown, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fadeUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

const INTERESTS = ['Campeonatos VIP', 'Campos Legendarios', 'Retiros Privados', 'Golf Corporativo', 'Padre e Hijo', 'Golf & Lifestyle'];

export default function ProfilePage() {
  const { user, loading: authLoading, login } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ phone: '', handicap: '', interests: [], preferred_destinations: [] });
  const [loadingProfile, setLoadingProfile] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile`, { credentials: 'include' });
      if (res.ok) {
        const p = await res.json();
        setProfile(p);
        setForm({ phone: p.phone || '', handicap: p.handicap || '', interests: p.interests || [], preferred_destinations: p.preferred_destinations || [] });
      }
    } catch { /* profile load failed silently */ }
    finally { setLoadingProfile(false); }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) return;
    if (user) loadProfile();
  }, [user, authLoading, loadProfile]);

  const handleSave = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const updated = await res.json();
        setProfile(prev => ({ ...prev, ...updated }));
        setEditing(false);
        toast.success('Perfil actualizado');
      }
    } catch { toast.error('Error al guardar'); }
  };

  const toggleList = (key, value) => setForm(f => ({ ...f, [key]: f[key].includes(value) ? f[key].filter(v => v !== value) : [...f[key], value] }));

  if (!authLoading && !user) {
    return (
      <div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center" data-testid="profile-login-prompt">
        <div className="text-center max-w-md px-6">
          <Crown className="w-12 h-12 text-zinc-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Area Privada</h1>
          <p className="text-base text-zinc-400 mb-8">Inicia sesion para acceder a tu perfil y preferencias de golf.</p>
          <button onClick={login} className="bg-white text-black px-8 py-4 text-sm font-medium hover:bg-zinc-200 transition-colors" data-testid="profile-login-btn">
            Iniciar Sesion con Google
          </button>
        </div>
      </div>
    );
  }

  if (loadingProfile || authLoading) {
    return <div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Header */}
        <motion.div {...fadeUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-12">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-[#0A120E] border-2 border-white/10 shrink-0">
            {profile?.picture ? <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" data-testid="profile-avatar" /> :
              <div className="w-full h-full flex items-center justify-center"><User size={32} className="text-zinc-600" /></div>}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="profile-name">{profile?.name}</h1>
            <p className="text-sm text-zinc-500">{profile?.email}</p>
          </div>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 px-5 py-2.5 text-sm transition-all flex items-center gap-2" data-testid="edit-profile-btn">
              <Edit3 size={14} /> Editar
            </button>
          ) : (
            <button onClick={handleSave} className="bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-zinc-200 transition-colors flex items-center gap-2" data-testid="save-profile-btn">
              <Save size={14} /> Guardar
            </button>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Personal Info */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-[#0A120E] border border-white/5 p-6 md:p-8">
              <h2 className="text-lg font-semibold text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Informacion de Golf</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Telefono</label>
                  {editing ? <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+52 444 000 0000"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30" data-testid="profile-phone-input" /> :
                    <p className="text-sm text-zinc-300 flex items-center gap-2"><Phone size={14} className="text-zinc-600" /> {profile?.phone || 'No especificado'}</p>}
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Handicap</label>
                  {editing ? <input type="text" value={form.handicap} onChange={e => setForm(f => ({ ...f, handicap: e.target.value }))} placeholder="Ej: 12"
                    className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30" data-testid="profile-handicap-input" /> :
                    <p className="text-sm text-zinc-300 flex items-center gap-2"><Trophy size={14} className="text-zinc-600" /> {profile?.handicap || 'No especificado'}</p>}
                </div>
              </div>
            </motion.div>

            {/* Interests */}
            <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="bg-[#0A120E] border border-white/5 p-6 md:p-8">
              <h2 className="text-lg font-semibold text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Experiencias de Interes</h2>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map(interest => {
                  const selected = form.interests.includes(interest);
                  return (
                    <button key={interest} onClick={() => editing && toggleList('interests', interest)} disabled={!editing}
                      className={`px-4 py-2 text-sm transition-all duration-300 ${selected ? 'bg-white text-black font-medium' : 'border border-white/10 text-zinc-400'} ${editing ? 'cursor-pointer hover:border-white/30' : 'cursor-default'}`}
                      data-testid={`interest-${interest.toLowerCase().replace(/ /g, '-')}`}>{interest}</button>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="p-6 md:p-8 bg-[#0A120E] border border-white/5">
              <Globe className="w-6 h-6 text-zinc-600 mb-4" />
              <p className="text-sm font-medium text-white mb-2">Tu Concierge Personal</p>
              <p className="text-xs text-zinc-500 mb-4">Cada experiencia es disenada exclusivamente para ti. Contacta a tu concierge para comenzar.</p>
              <Link to="/contacto" className="w-full bg-white text-black py-3 text-sm font-medium hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 block text-center" data-testid="profile-request-btn">
                Solicitar Experiencia
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.25 }} className="p-6 bg-[#0F291E] border border-white/5">
              <p className="text-sm font-medium text-white mb-2">WhatsApp Directo</p>
              <p className="text-xs text-zinc-400 mb-4">Atencion inmediata y personalizada.</p>
              <a href="https://wa.me/524442049874?text=Hola,%20soy%20miembro%20Elite%20Golf%20MX" target="_blank" rel="noreferrer"
                className="w-full bg-[#25D366] text-white py-3 text-sm font-medium hover:bg-[#20bd5a] transition-colors flex items-center justify-center gap-2" data-testid="profile-whatsapp-btn">
                Contactar Concierge
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
